import { isMediaPending } from "@anisync/types";
import {
  CaptionsIcon,
  LanguagesIcon,
  MaximizeIcon,
  MinimizeIcon,
  MinusIcon,
  PlusIcon,
  RotateCcwIcon,
  SkipForwardIcon,
  TypeIcon,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { MediaWarningList } from "../components/media-warnings";
import { SubtitlePicker } from "../components/subtitle-picker";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { WrongVersionButton } from "../components/wrong-version-button";
import {
  ApiError,
  type PlayAttempts,
  type PlayStatus,
  getPlayAttempts,
  getPlayStatus,
  retryPlay,
} from "../lib/api";
import { formatClock } from "../lib/format";
import { useUserPrefs } from "../lib/use-user-prefs";
import { cn } from "../lib/utils";
import {
  FONT_CHOICES,
  MARGIN_RANGE,
  OUTLINE_RANGE,
  SCALE_RANGE,
  isDefaultStyle,
} from "./ass-style";
import { BandwidthHint } from "./bandwidth-hint";
import { DownloadState } from "./download-state";
import { FailureState } from "./failure-state";
import type { NextEpisode } from "./next-episode";
import { NextEpisodeOverlay } from "./next-episode-overlay";
import { PlayerOsd, useChromeVisible } from "./osd";
import { ShortcutHelp, useShortcutHelp } from "./shortcut-help";
import { useImmersive } from "./use-immersive";
import { usePlaybackProgress } from "./use-playback-progress";
import { usePlayerShortcuts } from "./use-shortcuts";
import { skipLabel, useSkipRanges } from "./use-skip-ranges";
import { useSubtitles } from "./use-subtitles";
import { useVideoFit, videoFitStyle } from "./use-video-fit";

/**
 * Odtwarzacz.
 *
 * Odpytywanie stanu, a nie połączenie trwałe — to drugie dochodzi razem
 * z pokojami, gdzie postęp musi trafić do wielu osób naraz. Dla oglądania
 * samotnego odpytywanie raz na sekundę jest wystarczające i o rząd wielkości
 * prostsze; przerywamy je natychmiast po dojściu do stanu końcowego, żeby
 * otwarta karta nie generowała ruchu bez powodu.
 */

const POLL_INTERVAL_MS = 1000;

export function Player({
  mediaFileId,
  title,
  titleId = null,
  episodeNumber = null,
  trackProgress = false,
  onMediaChange,
  onEnded,
  nextEpisode = null,
  nextCountdown = null,
  nextCountdownFrom = 0,
  nextBusy = false,
  coverUrl = null,
  onPlayNext,
  onDismissNext,
}: {
  mediaFileId: string;
  title?: string;
  /** Kontekst katalogu — bez niego nie ma czego zapisywać w bibliotece. */
  titleId?: string | null;
  episodeNumber?: number | null;
  /** Włączane tylko dla zalogowanych; gość nie ma trwałej tożsamości. */
  trackProgress?: boolean;
  onMediaChange?: (mediaFileId: string) => void;
  /**
   * Odcinek dobiegł końca.
   *
   * Odtwarzacz sam nie decyduje, co dalej: w pokoju decyduje kworum, a solo —
   * ustawienie konta. Zdarzenie mówi tylko, że się skończyło.
   */
  onEnded?: () => void;
  /**
   * Propozycja następnego odcinka albo `null`, gdy nie ma czego proponować.
   *
   * Decyzję podejmuje EKRAN, nie odtwarzacz: wymaga danych o dostępności z katalogu,
   * a odtwarzacz świadomie nie wie nic o katalogu — działa też dla materiału
   * wgranego z dysku. Tutaj przychodzi już rozstrzygnięta.
   */
  nextEpisode?: NextEpisode | null;
  /** Sekundy do automatycznego przejścia albo `null`, gdy automat wyłączony. */
  nextCountdown?: number | null;
  /** Wartość początkowa odliczania — do narysowania paska. */
  nextCountdownFrom?: number;
  nextBusy?: boolean;
  coverUrl?: string | null;
  onPlayNext?: () => void;
  onDismissNext?: () => void;
}) {
  const [status, setStatus] = useState<PlayStatus | null>(null);
  const [attempts, setAttempts] = useState<PlayAttempts | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  /** Komunikat po „Zła wersja”, gdy plik został (zajęty pokój). */
  const [rejectNote, setRejectNote] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  /**
   * Na pełny ekran wchodzi KONTENER, nie element wideo — inaczej warstwa napisów
   * libass i sterowanie zostałyby za spodem obrazu.
   */
  const containerRef = useRef<HTMLDivElement>(null);
  /** Cały blok odtwarzacza — z niego mierzymy, ile miejsca zabiera to pod kadrem. */
  const rootRef = useRef<HTMLDivElement>(null);

  const isReady = status?.state === "ready" && status.streamUrl !== null;
  const { resumedFrom } = usePlaybackProgress({
    videoRef,
    titleId,
    episodeNumber,
    enabled: trackProgress,
    ready: isReady,
  });
  const subtitles = useSubtitles({ videoRef, mediaFileId, ready: isReady });
  const prefs = useUserPrefs();

  /**
   * Zakończenie odcinka w refie, nie w zależnościach efektu.
   *
   * Wołający przekazuje zwykle funkcję strzałkową tworzoną przy każdym renderze;
   * bez refu efekt odpinałby i podpinał `ended` kilkadziesiąt razy na minutę.
   */
  const onEndedRef = useRef(onEnded);
  useEffect(() => {
    onEndedRef.current = onEnded;
  }, [onEnded]);

  useEffect(() => {
    const video = videoRef.current;
    if (video === null || !isReady) return;
    const handler = () => onEndedRef.current?.();
    video.addEventListener("ended", handler);
    return () => video.removeEventListener("ended", handler);
  }, [isReady]);

  /**
   * Powrót do odtwarzania zdejmuje nakładkę.
   *
   * Nakładka przykrywa cały kadr, także przycisk pauzy pod nią, więc widz cofający
   * się do scenki po napisach zostałby z obrazem za przygaszoną warstwą i bez
   * oczywistego wyjścia. Zdarzenie `play` jest tu właściwym sygnałem, bo obejmuje
   * i przewinięcie, i klawisz.
   */
  const onDismissNextRef = useRef(onDismissNext);
  useEffect(() => {
    onDismissNextRef.current = onDismissNext;
  }, [onDismissNext]);

  useEffect(() => {
    const video = videoRef.current;
    if (video === null || nextEpisode === null) return;
    const handler = () => onDismissNextRef.current?.();
    video.addEventListener("play", handler);
    return () => video.removeEventListener("play", handler);
  }, [nextEpisode]);
  const skip = useSkipRanges({
    videoRef,
    titleId,
    episodeNumber,
    durationSec: status?.durationSec ?? null,
    autoSkip: prefs.autoSkipOpening,
    ready: isReady,
  });

  const immersive = useImmersive({ containerRef, videoRef, active: isReady });
  /**
   * Jedna widoczność dla OBU grup przycisków: tej u dołu i tej w narożniku.
   * Gdyby każda liczyła to sama, jedna znikałaby chwilę po drugiej.
   */
  const chromeVisible = useChromeVisible(containerRef, videoRef, isReady);
  const { open: helpOpen, setOpen: setHelpOpen } = useShortcutHelp();

  /**
   * Kadr ograniczony wysokością okna, nie tylko jego szerokością.
   *
   * Na pełnym ekranie ograniczenia nie ma — tam obraz MA wypełnić ekran.
   */
  const fit = useVideoFit({
    containerRef,
    rootRef,
    enabled: !immersive.isFullscreen,
    // Kadru nie ma w drzewie, dopóki materiał nie jest gotowy — a pomiar bez niego
    // nie ma czego zmierzyć ani czego obserwować.
    ready: isReady,
  });

  /**
   * Przełączenie napisów pamięta OSTATNIĄ włączoną ścieżkę.
   *
   * Bez tego `C` po wyłączeniu włączałoby pierwszą z listy, a nie tę, którą widz
   * przed chwilą wybrał — przy dwóch tłumaczeniach to różnica widoczna od razu.
   */
  const lastTrackRef = useRef<string | null>(null);
  useEffect(() => {
    if (subtitles.activeId !== null) lastTrackRef.current = subtitles.activeId;
  }, [subtitles.activeId]);

  const toggleSubtitles = useCallback(() => {
    if (subtitles.activeId !== null) {
      subtitles.select(null);
      return;
    }
    const fallback = lastTrackRef.current ?? subtitles.tracks[0]?.id ?? null;
    if (fallback !== null) subtitles.select(fallback);
  }, [subtitles]);

  usePlayerShortcuts({
    videoRef,
    enabled: isReady,
    hasActiveSubtitles: subtitles.activeId !== null,
    actions: {
      onToggleFullscreen: immersive.toggleFullscreen,
      onToggleSubtitles: toggleSubtitles,
      onSkip: skip.current !== null ? skip.skip : undefined,
      onSubtitleShift: subtitles.shiftBy,
    },
  });

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function poll(): Promise<void> {
      try {
        const next = await getPlayStatus(mediaFileId);
        if (cancelled) return;
        setStatus(next);
        setError(null);

        if (next.state === "failed") {
          // Tabelę prób pobieramy dopiero przy porażce — przy powodzeniu nikt
          // jej nie potrzebuje, a to dodatkowe żądanie na każde odtworzenie.
          getPlayAttempts(mediaFileId)
            .then((a) => {
              if (!cancelled) setAttempts(a);
            })
            .catch(() => undefined);
          return;
        }
        if (!isMediaPending(next.state)) return;

        timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
      } catch (e: unknown) {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : "Nie udało się odczytać stanu");
      }
    }

    setStatus(null);
    setAttempts(null);
    setRejectNote(null);
    void poll();

    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [mediaFileId]);

  const handleRetry = useCallback(
    (preferHost?: string) => {
      setRetrying(true);
      retryPlay(mediaFileId, preferHost)
        .then((next) => {
          // Ponowienie tworzy NOWY rekord materiału, więc trzeba przełączyć
          // identyfikator, inaczej odpytywalibyśmy skasowany wpis.
          onMediaChange?.(next.mediaFileId);
        })
        .catch((e: unknown) =>
          setError(e instanceof ApiError ? e.message : "Nie udało się ponowić"),
        )
        .finally(() => setRetrying(false));
    },
    [mediaFileId, onMediaChange],
  );

  if (error !== null && status === null) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (status === null) {
    /**
     * Zastępczy prostokąt też nie może wypchnąć strony poza okno.
     *
     * Tu nie ma czego mierzyć — kadru jeszcze nie ma, więc `useVideoFit` nie zna jego
     * położenia. `70svh` jest świadomym zgrubieniem na te kilkaset milisekund: dokładny
     * budżet wchodzi razem z obrazem.
     */
    return <Skeleton className="aspect-video max-h-[70svh] w-full rounded-xl" />;
  }

  if (status.state === "failed") {
    return (
      <FailureState
        attempts={attempts}
        retryable={true}
        busy={retrying}
        onRetry={handleRetry}
        errorCode={status.errorCode}
      />
    );
  }

  if (status.state !== "ready" || status.streamUrl === null) {
    return <DownloadState status={status} />;
  }

  return (
    <div ref={rootRef} className="flex flex-col gap-3">
      {/*
        Kadr wyśrodkowany przez RODZICA, nie własnym `mx-auto`.
        Na wąskim ekranie ten sam rodzic nosi `bleed-edge`, a `mx-auto` na kadrze
        walczyłoby z jego ujemnym marginesem o tę samą własność `margin-inline` —
        i wynik zależałby od kolejności reguł w arkuszu, nie od zamiaru.
      */}
      <div className="flex justify-center max-md:bleed-edge">
        {/*
          Kontener MUSI być pozycjonowany: libass wstawia własne płótno jako rodzeństwo
          elementu wideo i pozycjonuje je absolutnie. Bez `relative` napisy
          wylądowałyby względem strony, nie względem obrazu.

          Szerokość przychodzi z pomiaru (`useVideoFit`), a proporcja sama wyznacza
          wysokość — dzięki temu kadr nigdy nie wychodzi za dolną krawędź okna.
          Na wąskim ekranie obraz idzie DO KRAWĘDZI: ramka i zaokrąglenia zabierają
          tam kilka procent szerokości kadru, a to jedyna treść na ekranie.
          Na szerokim ramka zostaje, bo oddziela obraz od tła strony.
        */}
        <div
          ref={containerRef}
          style={videoFitStyle(fit)}
          className="relative aspect-video w-full overflow-hidden bg-black shadow-2xl shadow-black/40 md:rounded-xl md:border md:border-border"
        >
          {/*
          Bez `controls`: sterowanie rysujemy sami, bo natywny pasek nie umie
          pokazać podglądu klatki, a na telefonie ma cele wielkości kilku pikseli,
          których nie da się powiększyć — to element przeglądarki, nie strony.
        */}
          {/* biome-ignore lint/a11y/useMediaCaption: napisy ASS renderuje libass na
            osobnej warstwie — element `track` nie obsługuje tego formatu.
            Materiał bywa też z napisami wtopionymi w obraz. */}
          <video
            ref={videoRef}
            src={status.streamUrl}
            autoPlay
            preload="metadata"
            // Proporcję niesie kontener, więc obraz wypełnia go w całości. Materiał
            // o innym kształcie (4:3, kinowy) element wideo sam wpisuje w kadr —
            // `object-fit: contain` jest dla niego domyślne.
            className="h-full w-full bg-black"
          />

          {/*
          Stuknięcie w obraz wstrzymuje — przez PRAWDZIWY przycisk na nakładce,
          nie przez `onClick` na elemencie wideo. Obraz nie jest przyciskiem, więc
          czytnik ekranu nie ogłosiłby go jako czegoś, co da się nacisnąć, a sam
          `onClick` byłby niedostępny z klawiatury. Nakładka leży POD sterowaniem,
          żeby nie przechwytywała kliknięć w pasek.
        */}
          <button
            type="button"
            onClick={() => {
              const video = videoRef.current;
              if (video === null) return;
              if (video.paused) void video.play().catch(() => undefined);
              else video.pause();
            }}
            // Bez pierścienia fokusu na całym kadrze: obwódka wokół obrazu wygląda
            // jak błąd renderowania, a skrót `Space` i tak działa globalnie.
            className="focus-ring-own absolute inset-0 z-0 cursor-default outline-none"
            tabIndex={-1}
            aria-label="Wstrzymaj albo odtwórz"
          />

          <PlayerOsd
            videoRef={videoRef}
            durationSec={status.durationSec}
            spriteVttUrl={`/api/v1/media/${mediaFileId}/sprite.vtt`}
            visible={chromeVisible}
          />

          {/*
          Wymagane pasmo, na kilka sekund od startu.

          Materiał serwujemy jako plik, bez drabinki jakości — na wolnym łączu
          odtwarzacz nie schodzi niżej, tylko STOI. Bez tej liczby wygląda to na
          awarię serwera, choć jest ograniczeniem po stronie widza.
        */}
          <BandwidthHint
            videoRef={videoRef}
            sizeBytes={status.sizeBytes}
            durationSec={status.durationSec}
          />

          {/*
          Przycisk pomijania — nad obrazem, nad warstwą napisów.
          Automat jest WYŁĄCZONY domyślnie i włącza go ustawienie konta: opening
          bywa tym, po co ktoś włączył odcinek, a przy błędnym zakresie automat
          zabiera treść. Dlatego automatyczny przeskok zostawia po sobie propozycję
          cofnięcia, a nie tylko fakt dokonany.
        */}
          {skip.current !== null && (
            <Button
              variant="secondary"
              size="sm"
              onClick={skip.skip}
              className="absolute right-4 bottom-24 z-20 shadow-lg"
            >
              <SkipForwardIcon />
              {skipLabel(skip.current.kind)}
            </Button>
          )}

          {skip.autoSkipped !== null && (
            <div className="absolute right-4 bottom-24 z-20 flex items-center gap-2 rounded-lg bg-background/90 px-3 py-2 text-sm shadow-lg backdrop-blur-sm">
              <span>Pominięto {autoSkipNoun(skip.autoSkipped.kind)}</span>
              <Button variant="secondary" size="sm" onClick={skip.undoAutoSkip}>
                <RotateCcwIcon />
                Cofnij
              </Button>
            </div>
          )}

          {/*
          Sterowanie napisami i pełnym ekranem MUSI być wewnątrz kontenera.
          Poprzednia wersja trzymała je pod obrazem, więc na pełnym ekranie znikały
          — czyli w jedynym momencie, w którym są naprawdę potrzebne.
        */}
          <div
            className={cn(
              "absolute top-3 right-3 z-10 flex items-center gap-1 rounded-lg bg-background/70 p-1 backdrop-blur-sm",
              "transition-opacity duration-200 ease-tv",
              chromeVisible ? "opacity-100" : "pointer-events-none opacity-0",
            )}
          >
            {subtitles.tracks.length > 0 && (
              <Button
                variant={subtitles.activeId !== null ? "secondary" : "ghost"}
                size="icon"
                onClick={toggleSubtitles}
                aria-pressed={subtitles.activeId !== null}
                aria-label={subtitles.activeId !== null ? "Wyłącz napisy" : "Włącz napisy"}
                title="Napisy (C)"
              >
                <CaptionsIcon />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={immersive.toggleFullscreen}
              aria-label={immersive.isFullscreen ? "Wyjdź z pełnego ekranu" : "Pełny ekran"}
              title="Pełny ekran (F)"
            >
              {immersive.isFullscreen ? <MinimizeIcon /> : <MaximizeIcon />}
            </Button>
          </div>

          {/*
            Nakładka „następny odcinek" — ostatnia w kontenerze, bo przykrywa wszystko
            pozostałe. Warunek jest jeden i jest po stronie ekranu: gdy `nextEpisode`
            przychodzi jako `null`, nie ma czego proponować i nie ma nakładki.
          */}
          {nextEpisode !== null && onPlayNext !== undefined && onDismissNext !== undefined && (
            <NextEpisodeOverlay
              next={nextEpisode}
              secondsLeft={nextCountdown}
              countdownFrom={nextCountdownFrom}
              coverUrl={coverUrl}
              busy={nextBusy}
              onPlay={onPlayNext}
              onDismiss={onDismissNext}
            />
          )}
        </div>
      </div>

      {resumedFrom !== null && (
        <p className="text-sm text-muted-foreground">Wznowiono od {formatClock(resumedFrom)}</p>
      )}

      {/*
        Ostrzeżenia tuż pod obrazem, a nie w stopce: mają być zobaczone w pierwszych
        sekundach, zanim ktoś obejrzy połowę odcinka z napisami AI albo odcinek
        innej serii. Przycisk stoi obok, bo jest jedyną odpowiedzią na oba.
      */}
      <MediaWarningList warnings={status.warnings ?? []} />
      {rejectNote !== null && (
        <Alert variant="info">
          <AlertDescription>{rejectNote}</AlertDescription>
        </Alert>
      )}

      {/* Ściągawka skrótów: dwanaście klawiszy, o których dotąd nie było skąd
          się dowiedzieć. Otwiera ją `?`. */}
      <ShortcutHelp open={helpOpen} onClose={() => setHelpOpen(false)} />

      <SubtitleBar
        subtitles={subtitles}
        titleId={titleId}
        episodeNumber={episodeNumber}
        mediaFileId={mediaFileId}
      />

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
        {title !== undefined && <span className="font-medium text-foreground">{title}</span>}
        {status.durationSec !== null && (
          <span className="tabular-nums">{formatClock(status.durationSec)}</span>
        )}
        {status.playability !== null && <Badge variant="outline">{status.playability}</Badge>}
        {attempts?.chosenHost != null && <Badge variant="secondary">{attempts.chosenHost}</Badge>}
        {status.rejectable === true && (
          <WrongVersionButton
            mediaFileId={mediaFileId}
            onDone={(result) => {
              if (result.kept || onMediaChange === undefined) {
                // Pokój odtwarza ten plik albo odtwarzacz nie umie sam przejść na
                // nowy materiał — mówimy, co się stało, zamiast udawać zmianę.
                setRejectNote(
                  "Zapamiętano jako złą wersję. Ten plik zostaje, bo odtwarza go pokój — następne pobranie tego odcinka weźmie inną kopię.",
                );
                return;
              }
              // Nowy materiał to następna kopia w drodze; odtwarzacz pokaże jej pobieranie.
              onMediaChange(result.mediaFileId);
            }}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Sterowanie napisami: wybór ścieżki i przesunięcie.
 *
 * Osobny komponent, bo odtwarzacz i tak jest długi, a to spójna całość
 * z własnym stanem wejściowym. Nie pokazujemy niczego, gdy ścieżek nie ma —
 * puste sterowanie sugerowałoby, że napisy się nie wczytały.
 */
function SubtitleBar({
  subtitles,
  titleId,
  episodeNumber,
  mediaFileId,
}: {
  subtitles: ReturnType<typeof useSubtitles>;
  titleId: string | null;
  episodeNumber: number | null;
  mediaFileId: string;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  /**
   * Brak ścieżek NIE ukrywa całego paska, jeśli da się poszukać.
   *
   * Odcinek bez napisów to dokładnie ta sytuacja, w której szukanie jest potrzebne —
   * pierwsza wersja wracała tu `null` i chowała jedyny przycisk, który mógł pomóc.
   */
  const canSearch = titleId !== null && episodeNumber !== null;
  if (subtitles.tracks.length === 0 && !canSearch) return null;

  const active = subtitles.tracks.find((t) => t.id === subtitles.activeId);

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <CaptionsIcon className="size-4" />
        Napisy
      </span>

      <div className="flex flex-wrap gap-1">
        {subtitles.tracks.map((track) => (
          <Button
            key={track.id}
            variant={track.id === subtitles.activeId ? "secondary" : "ghost"}
            size="sm"
            onClick={() => subtitles.select(track.id)}
          >
            {track.label ?? track.lang.toUpperCase()}
          </Button>
        ))}
        <Button
          variant={subtitles.activeId === null ? "secondary" : "ghost"}
          size="sm"
          onClick={() => subtitles.select(null)}
        >
          Wyłączone
        </Button>
      </div>

      {/*
        Powód wyłączenia, jednym zdaniem.

        Bez tego widz ma polską ścieżkę na liście i wyłączone napisy, i nie ma jak
        zgadnąć, że to decyzja, a nie awaria — czyli dokładnie ten wzorzec, który ten
        projekt tępi: mechanizm, który wygląda inaczej, niż działa.
      */}
      {/*
        Ręczne szukanie napisów — tylko z kontekstem katalogu, bo animesub szuka po
        tytule i numerze odcinka. Materiał wgrany z dysku bez przypisania do tytułu
        nie ma czego podać, więc przycisku tam nie ma.
      */}
      {canSearch && (
        <Button variant="ghost" size="sm" onClick={() => setPickerOpen(true)}>
          <LanguagesIcon />
          Szukaj napisów
        </Button>
      )}

      {/*
        Po pobraniu odświeżamy listę i **włączamy nową ścieżkę od razu**: widz kliknął
        „weź te", więc chce je zobaczyć, a nie wybierać drugi raz z listy.
      */}
      {canSearch && titleId !== null && episodeNumber !== null && (
        <SubtitlePicker
          titleId={titleId}
          episodeNumber={episodeNumber}
          mediaFileId={mediaFileId}
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onFetched={(trackId) => {
            subtitles.reload();
            subtitles.select(trackId);
          }}
        />
      )}

      {subtitles.suppressedHardsub && subtitles.activeId === null && (
        <span className="text-xs text-muted-foreground">
          Materiał z polskiego źródła — napisy bywają wtopione w obraz, więc nakładka startuje
          wyłączona.
        </span>
      )}

      {active !== undefined && (
        <div className="flex items-center gap-1">
          {/* Przesunięcie przydaje się, gdy napisy są zsynchronizowane pod inne
              wydanie. Skróty `[` i `]` robią to samo bez sięgania do myszy. */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => subtitles.shiftBy(-0.25)}
            aria-label="Napisy wcześniej o ćwierć sekundy"
          >
            <MinusIcon />
          </Button>
          <span className="min-w-[4rem] text-center text-xs text-muted-foreground tabular-nums">
            {subtitles.offsetSec === 0
              ? "bez przesunięcia"
              : `${subtitles.offsetSec > 0 ? "+" : ""}${subtitles.offsetSec.toFixed(2)} s`}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => subtitles.shiftBy(0.25)}
            aria-label="Napisy później o ćwierć sekundy"
          >
            <PlusIcon />
          </Button>
        </div>
      )}

      {active !== undefined && <SubtitleLook subtitles={subtitles} />}

      {subtitles.loading && <span className="text-xs text-muted-foreground">wczytuję silnik…</span>}
      {subtitles.error !== null && (
        <span className="text-xs text-destructive">{subtitles.error}</span>
      )}
    </div>
  );
}

/**
 * Wygląd napisów: rozmiar, obrys, krój, wysokość.
 *
 * Zwinięte za jednym przyciskiem, bo to ustawienie **jednorazowe na urządzenie**:
 * raz się dobiera rozmiar do przekątnej i odległości od ekranu, a potem się do
 * tego nie wraca. Rozwinięte na stałe zajmowałoby miejsce sterowaniu, którego
 * używa się przy każdym odcinku.
 *
 * Wartości zapisują się od razu, bez przycisku „Zapisz": każdy ruch suwaka widać
 * na obrazie natychmiast, więc potwierdzanie tego, co już się stało, byłoby
 * dodatkowym krokiem po nic.
 */
function SubtitleLook({ subtitles }: { subtitles: ReturnType<typeof useSubtitles> }) {
  const [open, setOpen] = useState(false);
  const { style, setStyle, resetStyle } = subtitles;
  const custom = !isDefaultStyle(style);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        variant={open ? "secondary" : "ghost"}
        size="sm"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <TypeIcon />
        Wygląd
        {/* Kropka przy zmienionym wyglądzie: bez niej nie da się odróżnić „napisy
            wyglądają dziwnie, bo taki jest fansub" od „bo ktoś to przestawił". */}
        {custom && <span className="size-1.5 rounded-full bg-accent" aria-hidden="true" />}
      </Button>

      {open && (
        <div className="flex w-full flex-wrap items-center gap-x-5 gap-y-3 rounded-xl border border-border bg-card/60 p-3">
          <StyleSlider
            label="Rozmiar"
            value={style.scale}
            range={SCALE_RANGE}
            format={(v) => `${Math.round(v * 100)}%`}
            onChange={(scale) => setStyle({ scale })}
          />
          <StyleSlider
            label="Obrys"
            value={style.outline}
            range={OUTLINE_RANGE}
            format={(v) => (v === 0 ? "brak" : `×${v.toFixed(2)}`)}
            onChange={(outline) => setStyle({ outline })}
          />
          <StyleSlider
            label="Wysokość"
            value={style.marginBottom}
            range={MARGIN_RANGE}
            format={(v) => (v === 0 ? "jak w napisach" : `+${v}`)}
            onChange={(marginBottom) => setStyle({ marginBottom })}
          />

          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            Krój
            {/*
              Zwykły `select`, nie primityw Radiksa: lista ma sześć pozycji i stoi
              w warstwie nad wideo, gdzie natywna kontrolka systemu jest jedyną,
              która na pewno zmieści się na telefonie i zadziała pilotem.
            */}
            <select
              value={style.fontFamily ?? ""}
              onChange={(e) =>
                setStyle({ fontFamily: e.target.value === "" ? null : e.target.value })
              }
              className="focus-ring-own rounded-md border border-border bg-secondary px-2 py-1 text-foreground outline-none focus-visible:ring-[0.1875rem] focus-visible:ring-ring/50"
            >
              {FONT_CHOICES.map((choice) => (
                <option key={choice.label} value={choice.value ?? ""}>
                  {choice.label}
                </option>
              ))}
            </select>
          </label>

          {custom && (
            <Button variant="ghost" size="sm" onClick={resetStyle}>
              <RotateCcwIcon />
              Jak w napisach
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function StyleSlider({
  label,
  value,
  range,
  format,
  onChange,
}: {
  label: string;
  value: number;
  range: { min: number; max: number; step: number };
  format: (value: number) => string;
  onChange: (value: number) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-xs text-muted-foreground">
      <span className="min-w-[4.5rem]">{label}</span>
      <input
        type="range"
        min={range.min}
        max={range.max}
        step={range.step}
        value={value}
        onChange={(e) => onChange(Number.parseFloat(e.target.value))}
        className="h-1 w-28 accent-primary"
      />
      {/* Wartość liczbowa obok suwaka: sam suwak nie mówi, czy „duże" znaczy 120%
          czy 200%, a przy powrocie do ustawień to pierwsze pytanie. */}
      <span className="min-w-[5.5rem] tabular-nums">{format(value)}</span>
    </label>
  );
}

/** Nazwa pominiętego fragmentu w zdaniu „Pominięto …". */
function autoSkipNoun(kind: "op" | "ed" | "recap" | "preview"): string {
  switch (kind) {
    case "op":
      return "czołówkę";
    case "ed":
      return "tyłówkę";
    case "recap":
      return "streszczenie";
    case "preview":
      return "zapowiedź";
  }
}
